import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  useInfiniteQuery,
  useQueryClient
} from 'react-query'

import { addToast } from 'components/Toast/Toast'
import {
  createConversation,
  getConversationMessages
} from 'services/endpoints/message'

type UseConversationMessagesArgs = {
  conversationId: string | null
  setConversationId: Dispatch<SetStateAction<string | null>>
  otherUserId: string | null
  searchTerm?: string
  onRestrictedAccount?: () => void
}

export function useConversationMessages({
  conversationId,
  setConversationId,
  otherUserId,
  searchTerm = '',
  onRestrictedAccount
}: UseConversationMessagesArgs) {
  const [realtimeEnabled, setRealtimeEnabled] =
    useState(false)

  const [creatingConversation, setCreatingConversation] = useState(false)
  const [createConversationError, setCreateConversationError] =
    useState(false)

  const didInitialScrollRef = useRef(false)
  const queryClient = useQueryClient()

  const onRestrictedAccountRef = useRef(onRestrictedAccount)

  useEffect(() => {
    onRestrictedAccountRef.current = onRestrictedAccount
  }, [onRestrictedAccount])

  const normalizedSearch = searchTerm.trim()

  const query = useInfiniteQuery(
    [
      'conversation',
      conversationId,
      normalizedSearch
    ],
    async ({ pageParam = 1 }) =>
      await getConversationMessages(
        conversationId,
        {
          page: pageParam,
          search:
            normalizedSearch === ''
              ? null
              : normalizedSearch
        }
      ),
    {
      enabled: Boolean(conversationId),

      getNextPageParam: lastPage => {
        const current =
          lastPage?.meta?.current_page

        const last =
          lastPage?.meta?.last_page

        return current &&
          last &&
          current < last
          ? current + 1
          : undefined
      },

      refetchOnWindowFocus: false,
      staleTime: 30_000
    }
  )

  useEffect(() => {
    let cancelled = false

    const ensureConversation = async () => {
      if (conversationId) {
        setCreatingConversation(false)
        setCreateConversationError(false)
        return
      }

      if (
        typeof otherUserId !== 'string' ||
        otherUserId.trim() === ''
      ) {
        setCreatingConversation(false)
        return
      }

      setCreatingConversation(true)
      setCreateConversationError(false)

      try {
        const response = await createConversation(otherUserId)

        if (cancelled) return

        const newId = response?.data?.id

        if (
          typeof newId !== 'string' ||
          newId.trim() === ''
        ) {
          throw new Error('Invalid conversation response')
        }

        setConversationId(newId)

        void queryClient.invalidateQueries([
          'conversations'
        ])
      } catch (error: any) {
        if (cancelled) return

        setCreateConversationError(true)

        const status = error?.response?.status
        const message = error?.response?.data?.message

        if (
          status === 403 &&
          message === 'Your account is restricted'
        ) {
          onRestrictedAccountRef.current?.()
          return
        }

        addToast(
          'error',
          message || 'Failed to open conversation'
        )
      } finally {
        if (!cancelled) {
          setCreatingConversation(false)
        }
      }
    }

    void ensureConversation()

    return () => {
      cancelled = true
    }
  }, [
    conversationId,
    otherUserId,
    setConversationId,
    queryClient
  ])
  useEffect(() => {
    if (
      !query.isLoading &&
      query.data &&
      !realtimeEnabled
    ) {
      setRealtimeEnabled(true)
    }
  }, [
    query.isLoading,
    query.data,
    realtimeEnabled
  ])

  const queryMessages = useMemo(() => {
    if (!query.data?.pages) {
      return []
    }

    const merged =
      query.data.pages.flatMap(
        (page: any) => page?.data ?? []
      )

    const normalized = merged.map(
      (row: any) => {
        const message =
          row?.message ?? row

        const senderId =
          message?.sender_id ??
          message?.user_id ??
          row?.sender_id ??
          row?.user_id ??
          null

        return {
          ...row,
          message: {
            ...message,
            sender_id: senderId
          },
          sender_id: senderId
        }
      }
    )

    normalized.sort(
      (first: any, second: any) => {
        const firstTime = new Date(
          first?.message?.created_at ?? 0
        ).getTime()

        const secondTime = new Date(
          second?.message?.created_at ?? 0
        ).getTime()

        if (firstTime !== secondTime) {
          return firstTime - secondTime
        }

        const firstId = String(
          first?.message?.id ??
          first?.id ??
          ''
        )

        const secondId = String(
          second?.message?.id ??
          second?.id ??
          ''
        )

        return firstId.localeCompare(secondId)
      }
    )

    return normalized
  }, [query.data])


  const isCreatingOrLoading =
    creatingConversation ||
    (
      Boolean(conversationId) &&
      query.isLoading
    )

  const hasConversationError =
    createConversationError ||
    query.isError

  return {
    ...query,

    isLoading: isCreatingOrLoading,
    isError: hasConversationError,

    creatingConversation,
    createConversationError,

    displayedList: queryMessages,
    didInitialScrollRef,
    realtimeEnabled,
    queryClient
  }
}